import { Checkbox } from '@mui/material';
import { useState } from 'react';
import logo from '../../../assets/logos/aye-logo.png';
import PayNetFooter from '../../../shared/PayNetFooter';
import type {
  CardBrand,
  EWalletBrand
} from '../../../types/registration-payment.types';
import { AccountDetailsForm } from './components/AccountDetailsForm';
import { EnhanceCheckoutDialog } from './components/EnhanceCheckoutDialog';
import ImportantNotesDialog from './components/ImportantNotesDialog';
import { PaymentMethodCard } from './components/PaymentMethodCard';
import { SelectCardBrandStep } from './components/SelectCardBrandStep';
import { SelectEWalletBrandStep } from './components/SelectEWalletBrandStep';
import { SelectPaymentTypeStep } from './components/SelectPaymentTypeStep';
import { useRegistrationFlow } from './hooks/useRegistrationFlow';

const RegisterScreen = () => {
  const {
    // Form state
    phoneNumber,
    setPhoneNumber,
    countryPhoneCode,
    setCountryPhoneCode,
    name,
    setName,
    email,
    setEmail,
    nationality,
    setNationality,
    phoneVerificationToken,
    setPhoneVerificationToken,
    emailVerificationToken,
    setEmailVerificationToken,
    nameTouched,
    setNameTouched,
    emailTouched,
    setEmailTouched,
    nameError,
    emailError,
    // Prefill
    initialPhoneNumber,
    initialCountryCode,
    initialPhoneVerificationToken,
    // UI
    checked,
    setChecked,
    isDryRunChecked,
    setIsDryRunChecked,
    errorMessage,
    setErrorMessage,
    isSubmitting,
    showImportantNotes,
    setShowImportantNotes,
    // Step navigation
    paymentStep,
    selectedBrand,
    goToPaymentTypeSelection,
    goToCardBrandSelection,
    goToEWalletBrandSelection,
    goToAccountDetails,
    confirmBrandSelection,
    clearBrandSelection,
    // Actions
    handleSubmit,
    doRegistration,
    // Feature flags
    isRegisterDryRunFeatureEnabled,
    isEWalletEnabled,
    isDevBypassEnabled
  } = useRegistrationFlow();

  const phoneNumberError = () => false;
  const [showEnhanceDialog, setShowEnhanceDialog] = useState(false);

  const handleContinueClick = () => {
    if (!selectedBrand) {
      setShowEnhanceDialog(true);
    } else {
      handleSubmit();
    }
  };

  return (
    <>
      <div className="flex min-h-screen flex-col items-center bg-white">
        {/* Dev bypass banner — only visible when REACT_APP_DEV_BYPASS=true */}
        {isDevBypassEnabled && (
          <div className="w-full bg-yellow-400 py-1 text-center text-xs font-bold text-yellow-900">
            ⚠ DEV BYPASS MODE — validation & login guard disabled
          </div>
        )}
        {/* Logo — persists across all steps */}
        <img
          src={logo}
          alt="Aye Logo"
          className="mt-9 h-8 w-16 flex-shrink-0"
        />

        {/* Step content */}
        <div className="flex w-full flex-1 flex-col items-center px-6">
          {/* Account details stays mounted across payment sub-steps (toggled with
              `contents`/`hidden`) so transient child state — the entered phone
              number displayed in PhoneNumberInput and the OTP widget state —
              survives a trip into the payment flow and back. */}
          <div
            className={
              paymentStep === 'account-details' ? 'contents' : 'hidden'
            }
          >
            <>
              {/* Final step banner */}
              <div className="mt-6 w-full max-w-md">
                <div className="flex items-center gap-3 border-l-4 border-[#5CB891] bg-[#5CB891]/10 px-4 py-3">
                  <span className="text-xl">🎉</span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#5CB891]">
                      Final Step
                    </p>
                    <p className="text-sm font-bold text-gray-800">
                      Almost done!
                    </p>
                  </div>
                </div>
              </div>

              <AccountDetailsForm
                setCountryPhoneCode={setCountryPhoneCode}
                setPhoneNumber={setPhoneNumber}
                phoneNumberError={phoneNumberError}
                nationality={nationality}
                setNationality={setNationality}
                setName={setName}
                setEmail={setEmail}
                name={name}
                email={email}
                nameError={nameError}
                emailError={emailError}
                nameTouched={nameTouched}
                emailTouched={emailTouched}
                setNameTouched={setNameTouched}
                setEmailTouched={setEmailTouched}
                initialPhoneNumber={initialPhoneNumber}
                initialCountryCode={initialCountryCode}
                phoneNumber={phoneNumber}
                countryCode={countryPhoneCode}
                onPhoneVerified={(token) => {
                  setPhoneVerificationToken(token);
                  setErrorMessage(undefined);
                }}
                onEmailVerified={(token) => {
                  setEmailVerificationToken(token);
                  setErrorMessage(undefined);
                }}
                onPhoneReset={() => setPhoneVerificationToken(undefined)}
                onEmailReset={() => setEmailVerificationToken(undefined)}
                preVerifiedPhoneToken={
                  phoneVerificationToken ?? initialPhoneVerificationToken
                }
                preVerifiedEmailToken={emailVerificationToken}
              />

              <PaymentMethodCard
                selectedBrand={selectedBrand}
                onAdd={goToPaymentTypeSelection}
                onClear={clearBrandSelection}
              />

              {/* Account details footer: error, T&C, submit */}
              <div className="w-full max-w-md px-6 sm:px-0">
                {errorMessage && (
                  <p className="mt-3 text-center text-sm text-red-500">
                    {errorMessage}
                  </p>
                )}

                <div className="mt-6">
                  <div className="flex flex-row items-center justify-start">
                    <Checkbox
                      id="terms-checkbox"
                      checked={checked}
                      color="success"
                      onChange={() => setChecked(!checked)}
                    />
                    <label
                      htmlFor="terms-checkbox"
                      className="text-sm text-gray-700"
                    >
                      I have read and agree to the&nbsp;
                      <a
                        href="https://aye-ai.org/user-tc/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        terms and conditions
                      </a>
                    </label>
                  </div>

                  {isRegisterDryRunFeatureEnabled && (
                    <div className="mt-2 flex flex-row items-center justify-start">
                      <Checkbox
                        id="dry-run-checkbox"
                        checked={isDryRunChecked}
                        color="success"
                        onChange={() => setIsDryRunChecked(!isDryRunChecked)}
                      />
                      <label
                        htmlFor="dry-run-checkbox"
                        className="text-sm text-gray-700"
                      >
                        Run test registration{' '}
                        <span className="text-red-700">
                          (User account will not be created)
                        </span>
                      </label>
                    </div>
                  )}
                </div>

                <div className="mt-5 flex justify-center">
                  <button
                    type="button"
                    disabled={!checked || isSubmitting}
                    aria-label="Continue"
                    onClick={handleContinueClick}
                    className={`w-48 rounded-2xl bg-[#5CB891] px-2 py-4 font-semibold text-white transition-all duration-300 ease-out hover:bg-[#4aa07c] ${
                      !checked || isSubmitting
                        ? 'cursor-not-allowed opacity-50'
                        : ''
                    }`}
                  >
                    {isSubmitting ? 'Processing...' : 'Continue'}
                  </button>
                </div>
              </div>
            </>
          </div>

          {paymentStep === 'select-payment-type' && (
            <SelectPaymentTypeStep
              onSelectCards={goToCardBrandSelection}
              onSelectEWallets={goToEWalletBrandSelection}
              onSkipPayment={() => {
                clearBrandSelection();
                goToAccountDetails();
              }}
              onBack={goToAccountDetails}
              isEWalletEnabled={isEWalletEnabled}
            />
          )}

          {paymentStep === 'select-card-brand' && (
            <SelectCardBrandStep
              initialBrand={selectedBrand as CardBrand | null}
              onConfirm={confirmBrandSelection}
              onBack={goToPaymentTypeSelection}
            />
          )}

          {paymentStep === 'select-ewallet-brand' && (
            <SelectEWalletBrandStep
              initialBrand={selectedBrand as EWalletBrand | null}
              onConfirm={confirmBrandSelection}
              onBack={goToPaymentTypeSelection}
            />
          )}
        </div>

        {/* PayNet footer — always visible */}
        <div className="my-4 flex justify-center">
          <PayNetFooter />
        </div>
      </div>

      <ImportantNotesDialog
        open={showImportantNotes}
        onClose={() => setShowImportantNotes(false)}
        onComplete={doRegistration}
      />

      <EnhanceCheckoutDialog
        open={showEnhanceDialog}
        onClose={() => setShowEnhanceDialog(false)}
        onLinkPayment={() => {
          setShowEnhanceDialog(false);
          goToPaymentTypeSelection();
        }}
        onContinueWithout={() => {
          setShowEnhanceDialog(false);
          handleSubmit();
        }}
        onBack={() => setShowEnhanceDialog(false)}
      />
    </>
  );
};

export default RegisterScreen;
